import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import favicon from "../assets/favicon_new.png";

import {
  Send,
  MessageCircle,
  CheckCircle,
} from "lucide-react";

const SupportPage = ({
  activeSection,
  setActiveSection,
  isDarkMode,
  setIsDarkMode,
}) => {
  const [supportCategory, setSupportCategory] = useState("general");
  const [supportMessage, setSupportMessage] = useState("");
  const [sendingSupport, setSendingSupport] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSupportSubmit = async () => {
    if (!supportMessage.trim()) return;
    setSendingSupport(true);

    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 2000));

    setSendingSupport(false);
    setSubmitted(true);

    // Reset form after success message
    setTimeout(() => {
      setSubmitted(false);
      setSupportMessage("");
    }, 3000);
  };

  const categoryOptions = [
    {
      value: "integration",
      label: "Integration Support",
      icon: "⛓️",
      description: "Integration assistance",
    },
    {
      value: "general",
      label: "General Support",
      icon: "💬",
      description: "General questions and assistance",
    },
    {
      value: "technical",
      label: "Technical Issue",
      icon: "🔧",
      description: "Bug reports and technical problems",
    },
    {
      value: "billing",
      label: "Billing Question",
      icon: "💳",
      description: "Payment and subscription inquiries",
    },
    {
      value: "feature",
      label: "Feature Request",
      icon: "💡",
      description: "Suggest new features or improvements",
    },
  ];

  if (activeSection !== "support") return null;

  return (
    <div className="transition-colors duration-300">
      {/* Header */}
      <div className="max-w-2xl mx-auto px-1 py-3 items-center justify-center">
        <h1 
        className={`text-3xl flex items-center justify-center font-bold bg-clip-text text-transparent
        ${isDarkMode?"bg-white":"bg-black"}
        `}>
          Customer Support
        </h1>
      </div>

      {/* Main Content */}
      <div className="max-w-3xl mx-auto px-8 py-8">
        <div
          className={`rounded-2xl border transition-all duration-300 ${
            isDarkMode
              ? "bg-slate-800/50 border-slate-700/50 shadow-xl"
              : "bg-white border-slate-200/60 shadow-lg"
          }`}
        >
          {/* Form Header */}
          <div className="bg-blue-500 p-3 text-white rounded-t-2xl">
            <div className="flex items-center space-x-2">
              <div className="w-9 h-9 rounded-lg bg-white/20 flex items-center justify-center">
                <MessageCircle size={17} />
              </div>
              <div>
                <h2 className="text-base font-bold">Get Help</h2>
                <p className="text-indigo-100 text-xs">
                  Tell us how we can assist you
                </p>
              </div>
            </div>
          </div>

          {/* Success Message */}
          <AnimatePresence>
            {submitted && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className={`p-3 border-b ${
                  isDarkMode
                    ? "bg-emerald-900/30 border-emerald-700/50"
                    : "bg-emerald-50 border-emerald-200/60"
                }`}
              >
                <div
                  className={`flex items-center space-x-3 ${
                    isDarkMode ? "text-emerald-300" : "text-emerald-800"
                  }`}
                >
                  <CheckCircle size={20} />
                  <div>
                    <span className="font-semibold">Request submitted!</span>
                    <p className="text-sm">We'll respond within 24 hours.</p>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Form */}
          <div className="p-5 space-y-3">
            {/* Category */}
            <div>
              <label
                className={`block text-sm font-semibold mb-1 ${
                  isDarkMode ? "text-slate-300" : "text-slate-700"
                }`}
              >
                How can we help?
              </label>
              <select
                value={supportCategory}
                onChange={(e) => setSupportCategory(e.target.value)}
                className={`w-full px-2 py-1.5 text-sm rounded-lg border transition-colors ${
                  isDarkMode
                    ? "bg-slate-700/50 border-slate-600 text-white"
                    : "bg-slate-50 border-slate-200 text-slate-800"
                }`}
              >
                {categoryOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.icon} {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Message */}
            <div>
              <label
                className={`block text-sm font-semibold mb-1 ${
                  isDarkMode ? "text-slate-300" : "text-slate-700"
                }`}
              >
                Message
              </label>
              <textarea
                value={supportMessage}
                onChange={(e) => setSupportMessage(e.target.value)}
                placeholder="Describe your issue or question..."
                rows="3"
                className={`w-full px-2 py-1.5 text-sm rounded-lg border transition-colors resize-none ${
                  isDarkMode
                    ? "bg-slate-700/50 border-slate-600 text-white placeholder-slate-400"
                    : "bg-slate-50 border-slate-200 text-slate-800 placeholder-slate-500"
                }`}
                required
              />
            </div>

            {/* Submit Button */}
            <button
              onClick={handleSupportSubmit}
              disabled={
                sendingSupport ||
                !supportMessage.trim()
              }
              className={`w-full px-3 py-2 text-sm rounded-lg font-semibold transition-all duration-200 flex items-center justify-center space-x-2 ${
                sendingSupport ||
                !supportMessage.trim()
                  ? isDarkMode
                    ? "bg-slate-600/50 text-slate-400 cursor-not-allowed"
                    : "bg-slate-200 text-slate-500 cursor-not-allowed"
                  : "bg-blue-500 text-white shadow-md hover:shadow-lg hover:bg-blue-600"
              }`}
            >
              {sendingSupport ? (
                <>
                  <div className="relative w-3 h-3 mb-1">
                    <motion.div
                      className="w-full h-full"
                      animate={{ rotate: 360 }}
                      transition={{
                        duration: 2,
                        ease: "linear",
                        repeat: Infinity,
                      }}
                    >
                      <img 
                        src={favicon} 
                        alt="Loading" 
                        className="w-full h-full object-contain" 
                        onError={(e) => {
                          console.log('Image failed to load:', e.target.src);
                          e.target.style.display = 'none';
                        }}
                      />
                    </motion.div>
                  </div>
                  <span>Sending...</span>
                </>
              ) : (
                <>
                  <Send size={14} />
                  <span>Send Request</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupportPage;